import type { QueryClient } from "@tanstack/react-query";
import { invalidateNotifications } from "web_myplann/Api/Notifications/notificatioKeys";
import { openQuestionnaireFromPush } from "web_myplann/Api/Nsure/openQuestionnaire";
import { patchNotificationsIdReadV2 } from "web_myplann/Infrastructure/Api/Api";
import { handleCall } from "web_myplann/Infrastructure/Api/ApiUtils";
import { AppRouting } from "web_myplann/Infrastructure/Utils/UrlUtils";
import {
	fallbackToNotificationList,
	getDestinationRoute,
	isWebDestination,
	navigateToSource,
} from "web_myplann/Utils/WebViewHandlers/NotificationDestinations";
import {
	getMessageId,
	isDuplicate,
	sendReceipt,
} from "web_myplann/Utils/WebViewHandlers/NotificationReceipt";
import {
	type NavigateNotificationRequest,
	NavigationDestinationType,
	parseNotificationId,
} from "web_myplann/Utils/WebViewModel";

const markNotificationRead = (id: number, queryClient: QueryClient) => {
	void handleCall(patchNotificationsIdReadV2(id), {
		skipToastFor: true,
	}).then((result) => {
		if (result.isSuccess) {
			invalidateNotifications(queryClient);
		}
	});
};

export const handleNotificationNavigation = async (
	message: NavigateNotificationRequest,
	queryClient: QueryClient,
	navigate: (path: string) => void,
) => {
	const messageId = getMessageId(message);
	if (messageId) {
		sendReceipt(messageId);
		if (isDuplicate(messageId)) {
			return;
		}
	}

	const { payload } = message;
	const notificationId = parseNotificationId(payload.id);

	if (notificationId !== undefined) {
		markNotificationRead(notificationId, queryClient);
	}

	if (payload.type === NavigationDestinationType.investmentQuestionnaire) {
		const opened = await openQuestionnaireFromPush(
			{
				notificationId,
				clientType: payload.clientType,
				identificationNumber: payload.identificationNumber,
			},
			queryClient,
		);
		if (!opened) {
			navigate(AppRouting.MyPlann.Notifications.path());
		}
		return;
	}

	const route = getDestinationRoute(payload.type);
	if (route) {
		navigateToSource(route, payload, navigate);
		return;
	}

	if (isWebDestination(payload.type)) {
		fallbackToNotificationList(
			"Unhandled notification navigation type",
			payload,
			navigate,
		);
	}
};
